import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ownerReportApi from '../services/ownerReportApi';
import ReportMasthead from '../components/revenue/MonthlyReports/ReportMasthead';
import MonthlyReportView from '../components/revenue/MonthlyReports/MonthlyReportView';
import '../styles/MonthlyReports.css';
import '../styles/OwnerReportPrint.css';

/**
 * The owner monthly report as a printable DOCUMENT — `/finance/reports/:id/print`.
 *
 * ── Why a route of its own ───────────────────────────────────────────────────
 * Printing the report from `/finance` printed the whole dashboard: three pages of
 * charts and alert panels ahead of the report, because the print stylesheet there
 * can only hide what it knows about. Printing a *document* means rendering only
 * the document. This page therefore mounts NO layout — no DesktopLayout, no nav,
 * no sidebar — and everything on it except the toolbar is meant to reach paper.
 *
 * ── Access ───────────────────────────────────────────────────────────────────
 * `AuthGuard` only, exactly like `/finance`. No role check is added here on
 * purpose: the boundary is the API, which scopes an owner to their own released
 * reports as a DB filter and answers 404 otherwise. A second, cosmetic gate in the
 * browser would be a second definition of the rule, free to drift from the real one.
 *
 * ── Fonts before paper ───────────────────────────────────────────────────────
 * Auto-print waits for `document.fonts.ready`. Printing sooner produces a PDF set
 * in whatever face the operating system supplied — which is the exact defect the
 * self-hosted Tajawal exists to remove (styles/OwnerReportPrint.css).
 *
 * This same view becomes the server-side template when automated delivery arrives
 * (tasks/owner-monthly-report.md §7 Phase 5); nothing here assumes a human.
 */
const OwnerReportPrint = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Launched from the report ("طباعة التقرير") rather than opened directly, so the
  // print dialog is expected. A bare URL renders the document and waits — never
  // open a modal dialog someone did not ask for.
  const autoPrint = searchParams.get('auto') === '1';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Page geometry, injected for the lifetime of this route.
  //
  // `@page` cannot be scoped by a selector, and the build bundles every imported
  // stylesheet into one file — so writing this rule in OwnerReportPrint.css would
  // silently impose A4 and these margins on every printable page in the product.
  // A style element added on mount and removed on unmount keeps it to the one
  // route that means it.
  //
  // No space is reserved for a running header: Chrome ignores the CSS margin boxes
  // that would draw one, which is why page 1 carries the masthead and continuation
  // pages are plain (tasks/owner-monthly-report.md §19.4).
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-owner-report-page', '');
    style.textContent = '@page { size: A4 portrait; margin: 16mm 14mm; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await ownerReportApi.getReport(reportId);
        if (!cancelled) setReport(res?.data?.report || null);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.status === 404
              ? 'التقرير غير موجود أو غير متاح'
              : 'تعذّر تحميل التقرير'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [reportId]);

  const print = useCallback(() => {
    // Everything the page draws with, THEN paper.
    //
    // Fonts alone were not enough: the first print came out with the logo missing.
    // `window.print()` snapshots whatever has been painted, and an image that has
    // not finished decoding is simply absent — silently, with no error and no gap
    // in the layout to notice. Waiting on `decode()` is what makes that impossible;
    // it also covers any image added to this document later.
    //
    // Both waits degrade to "print anyway" rather than "never print": a font or an
    // image that fails is a worse-looking document, not a broken button.
    const fonts = document.fonts?.ready || Promise.resolve();
    const images = Promise.all(
      Array.from(document.images).map(img =>
        img.decode ? img.decode().catch(() => {}) : Promise.resolve()
      )
    );

    Promise.all([fonts, images]).catch(() => {}).then(() => window.print());
  }, []);

  useEffect(() => {
    if (!autoPrint || !report) return;
    print();
  }, [autoPrint, report, print]);

  if (loading) {
    return (
      <div className="report-print-page" dir="rtl">
        <div className="report-print-state">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
          <p>جاري تحضير التقرير للطباعة</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-print-page" dir="rtl">
        <div className="report-print-state">
          <p>{error || 'التقرير غير موجود أو غير متاح'}</p>
          <button type="button" className="btn-print" onClick={() => navigate('/finance')}>
            العودة إلى التقارير
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-print-page" dir="rtl">
      <div className="report-print-toolbar">
        <button type="button" className="btn-print" onClick={print}>
          <i className="bi bi-printer" aria-hidden="true"></i>
          طباعة التقرير
        </button>
        <button type="button" className="btn-back" onClick={() => navigate('/finance')}>
          <i className="bi bi-arrow-right" aria-hidden="true"></i>
          رجوع
        </button>
        {/* The browser's own header and footer print the page URL and the time.
            Only the print dialog can switch them off, so say where the switch is
            instead of leaving an owner with localhost in their document. */}
        <p className="report-print-hint">
          في نافذة الطباعة، أزل علامة «الرؤوس والتذييلات» للحصول على نسخة نظيفة
        </p>
      </div>

      <div className="report-document">
        <ReportMasthead
          periodMonth={report.periodMonth}
          ownerName={report.ownerName}
          propertyCount={report.propertyCount}
          generatedAt={report.generatedAt}
        />
        <MonthlyReportView report={report} variant="print" />
      </div>
    </div>
  );
};

export default OwnerReportPrint;
